import React, { useEffect, useState, useMemo} from 'react'
import axios from 'axios'
import { BASE_URL, STUDY_ABBREVIATION_MAP } from '../../utils/constants';
import { MdExitToApp } from "react-icons/md";
import { Table, Button } from 'react-bootstrap'
import { Oval } from "react-loader-spinner"
import { useNavigate, Link } from 'react-router-dom';
import moment from 'moment';
import _ from "underscore"
import { AiFillWarning, AiFillCaretUp, AiFillCaretDown } from "react-icons/ai";
import Pagination from '../../components/Pagination/Pagination';

import "./PatientComplications.scss"

const PatientComplications = ({ user, logUserOut }) => {
  const [loading, setLoading] = useState(false)
  const [complicationEvents, setComplicationEvents] = useState([])
  const [sorting, setSorting] = useState([{ field: 'customPatientId', ascending: true }])
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const navigate = useNavigate();
  const config = {
    headers: {
      Authorization: `Bearer ${user.token}`,
    },
  }

  useEffect(() => {
    const getNonCompliantPatients = async () => {
      try {
        setLoading(true)
        const response = await axios.get(`${BASE_URL}/api/v1/event/all-complication-events/`, config)
        const complicationEvents = response.data.complicationEvents
        setComplicationEvents(complicationEvents)
        setLoading(false)
      }
      catch (error) {
        console.log(error)
        const message = error.response && error.response.data.message ? error.response.data.message : error.message
        if (error.response && (error.response.status === 401 || message === 'Not authorized, token failed')) {
          logUserOut()
        }
      }
    }
    getNonCompliantPatients()
  }, [])

  // Multi-sort function
  const multiSort = (a, b) => {
    for (let sortRule of sorting) {
      const { field, ascending } = sortRule;
      let aValue = a[field];
      let bValue = b[field];
      
      // Handle date fields
      if (field === 'eventDate') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      // Handle string fields
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
      }
      if (typeof bValue === 'string') {
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) return ascending ? -1 : 1;
      if (aValue > bValue) return ascending ? 1 : -1;
    }
    return 0;
  };

  // Sort the complicationEvents using multi-sort
  const sortedData = useMemo(() => {
    const dataToSort = complicationEvents;
    return [...dataToSort].sort(multiSort);
  }, [sorting, complicationEvents])

  const handleSort = (field, event) => {
    const isShiftPressed = event.shiftKey;
    const isCtrlPressed = event.ctrlKey || event.metaKey;

    if (isCtrlPressed) {
      setSorting([{ field: 'customPatientId', ascending: true }]);
      return;
    }

    setSorting(prevSorting => {
      const existingSortIndex = prevSorting.findIndex(s => s.field === field);
      
      if (existingSortIndex > -1) {
        return prevSorting.map((sortItem, index) => {
          if (index === existingSortIndex) {
            return { ...sortItem, ascending: !sortItem.ascending };
          }
          return sortItem;
        });
      } else {
        if (isShiftPressed) {
          return [...prevSorting, { field, ascending: true }];
        } else {
          return [{ field, ascending: true }];
        }
      }
    });
    setCurrentPage(1); // Reset to page 1 when sorting changes
  };

  const renderSortIndicator = (field) => {
    const sortIndex = sorting.findIndex(s => s.field === field);
    if (sortIndex === -1) return "";
    
    const { ascending } = sorting[sortIndex];
    return (
      <>
        {ascending ? <AiFillCaretUp /> : <AiFillCaretDown />}
        {sorting.length > 1 && <span className="sort-priority">{sortIndex + 1}</span>}
      </>
    );
  };

  const resetSorting = () => {
    setSorting([{ field: 'customPatientId', ascending: true }]);
  };

  // Logic to handle UI-side pagination
  const totalItems = sortedData.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = sortedData.slice(startIndex, endIndex);

  return (
    <div className="patient-complications">
      {loading ? (<Oval
          height={80}
          width={80}
          color="#4caeff"
          wrapperStyle={{}}
          wrapperClass="loader"
          visible={true}
          ariaLabel='oval-loading'
          secondaryColor="#4caeff"
          strokeWidth={2}
          strokeWidthSecondary={2}
        />
        ) : (
        <>
          <div className="patient-complications-header">
            <h2 className="logo"><AiFillWarning /> Patient Complications {complicationEvents.length > 0? `(${complicationEvents.length})` : ""}</h2>
            {sorting.length > 1 && (
              <Button variant="outline-primary" size="sm" onClick={resetSorting} className="reset-sort-btn">
                Reset Sort
              </Button>
            )}
          </div>
          <div className="patient-complications-table">
            <Table striped bordered={false} hover>
              <thead>
                <tr>
                  <th className='custom-id' onClick={(e) => handleSort("customPatientId", e)}>Patient ID {renderSortIndicator("customPatientId")}</th>
                  <th className='study' onClick={(e) => handleSort("study", e)}>Study {renderSortIndicator("study")}</th>
                  <th className='event-date' onClick={(e) => handleSort("eventDate", e)}>Event Date {renderSortIndicator("eventDate")}</th>
                  <th className='actions'>Actions</th>
                  <th className='action-buttons'></th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? currentItems.map((complicationEvent, index) => {
                  return (
                    <tr key={index}>
                      <td className="custom-id">{complicationEvent.customPatientId}</td>
                      <td className="study">{STUDY_ABBREVIATION_MAP[complicationEvent.study]} ({complicationEvent.treatmentSide[0]})</td>
                      <td className="event-date">{moment(complicationEvent.eventDate).format("DD/MM/YYYY")}</td>
                      <td className="actions">
                        <Link to={`/patients/${complicationEvent.customPatientId}/events/complication/${complicationEvent.eventId}/edit`}>View Complication</Link></td>
                      <td className='action-buttons'>
                        <div>
                          <Button variant="primary" onClick={() => navigate(`/patients/${complicationEvent.customPatientId}`)}><MdExitToApp /></Button>
                        </div>
                      </td>
                    </tr>
                  )
                }) : <tr><td colSpan={100} style={{textAlign: "center"}}>No complications recorded</td></tr>}
              </tbody>
            </Table>
          </div>
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
          />
        </>)}    
    </div>
  )
}

export default PatientComplications